import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, getToken } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STATE_LABEL = {
  planning: "Planning", scheduled: "Scheduled", deployed: "Deployed",
  verified: "Verified", rolled_back: "Rolled back", cancelled: "Cancelled",
};

const isoLocal = (daysAhead, hour) => {
  const d = new Date();
  d.setDate(d.getDate() + daysAhead);
  d.setHours(hour, 0, 0, 0);
  return d.toISOString().slice(0, 16);
};

export default function Releases() {
  const [board, setBoard] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [gate, setGate] = useState(null);
  const [where, setWhere] = useState(null);
  const [notes, setNotes] = useState(null);
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState(null);
  const [override, setOverride] = useState("");
  const [note, setNote] = useState("");
  const [form, setForm] = useState({
    title: "", type_key: "", environment: "production", rollback_plan: "",
    start: isoLocal(3, 20), end: isoLocal(3, 23),
  });
  const navigate = useNavigate();

  const load = useCallback((selectedId) => {
    apiFetch("/releases/board").then(async (r) => {
      if (!r.ok) { setMessage("Releases are not available to you"); return; }
      const body = await r.json();
      setBoard(body);
      setForm((f) => ({ ...f, type_key: f.type_key || body.release_types[0]?.key || "" }));
    });
    apiFetch("/releases/performance?days=90").then(async (r) => {
      if (r.ok) setPerformance(await r.json());
    });
    apiFetch("/releases/where").then(async (r) => {
      if (r.ok) setWhere(await r.json());
    });
    if (selectedId) {
      apiFetch(`/releases/${selectedId}/readiness`).then(async (r) => {
        if (r.ok) setGate(await r.json());
      });
    }
  }, []);

  useEffect(() => {
    if (!getToken()) { navigate("/login"); return; }
    load(null);
  }, [navigate, load]);

  const open = (release) => {
    setNotes(null);
    setCopied(false);
    apiFetch(`/releases/${release.id}/readiness`).then(async (r) => {
      if (r.ok) { setGate(await r.json()); setMessage(null); setOverride(""); setNote(""); }
    });
  };

  const loadNotes = async () => {
    const r = await apiFetch(`/releases/${gate.release.id}/notes`);
    if (!r.ok) { setMessage("Release notes are not available to you"); return; }
    setNotes(await r.json());
    setCopied(false);
  };

  const copyNotes = async () => {
    try {
      await navigator.clipboard.writeText(notes.markdown);
      setCopied(true);
    } catch {
      setMessage("Your browser would not let the page copy — select the notes and copy them.");
    }
  };

  const create = async () => {
    const r = await apiFetch("/releases", {
      method: "POST",
      body: JSON.stringify({
        title: form.title, type_key: form.type_key, environment: form.environment,
        rollback_plan: form.rollback_plan,
        window: { start: new Date(form.start).toISOString(),
                  end: new Date(form.end).toISOString() },
      }),
    });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) {
      setMessage(typeof body.detail === "string" ? body.detail : "Could not create that release");
      return;
    }
    setMessage(`${body.number} created — group the changes it delivers`);
    setForm({ ...form, title: "", rollback_plan: "" });
    load(body.id);
    open(body);
  };

  const toggleChange = async (changeId) => {
    const current = gate.changes.map((c) => c.id);
    const next = current.includes(changeId)
      ? current.filter((c) => c !== changeId) : [...current, changeId];
    const r = await apiFetch(`/releases/${gate.release.id}/changes`, {
      method: "PUT", body: JSON.stringify({ change_ids: next }),
    });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) {
      setMessage(typeof body.detail === "string" ? body.detail : "Could not group that change");
      return;
    }
    setGate(body);
    setMessage(null);
    load(gate.release.id);
  };

  const act = async (path, body, okMessage) => {
    const r = await apiFetch(`/releases/${gate.release.id}${path}`, {
      method: "POST", body: JSON.stringify(body || {}),
    });
    const out = await r.json().catch(() => ({}));
    if (!r.ok) {
      const detail = out.detail;
      setMessage(typeof detail === "string" ? detail
        : `${detail?.message || "That is not possible"} ${detail?.order ? `Order: ${detail.order.join(" → ")}` : ""}`);
      return;
    }
    setGate(out);
    setMessage(out.message || okMessage);
    load(gate.release.id);
  };

  const schedule = async () => {
    const r = await apiFetch(`/releases/${gate.release.id}/schedule`, {
      method: "POST", body: JSON.stringify({ override_reason: override || null }),
    });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) {
      const detail = body.detail;
      setMessage(typeof detail === "string" ? detail
        : `${detail?.message || "Not ready"} ${detail?.blocked_reason || ""} — ${detail?.how_to_proceed || ""}`);
      return;
    }
    setGate(body);
    setMessage("Scheduled — the window is now on the operations calendar");
    setOverride("");
    load(gate.release.id);
  };

  const outcome = async (value) => {
    const r = await apiFetch(`/releases/${gate.release.id}/outcome`, {
      method: "POST", body: JSON.stringify({ outcome: value, note }),
    });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) {
      setMessage(typeof body.detail === "string" ? body.detail : "Could not record that");
      return;
    }
    setGate(body);
    setMessage(`Recorded as ${STATE_LABEL[value] || value}`);
    setNote("");
    load(gate.release.id);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" data-testid="releases-title">Releases</h1>
          <p className="text-sm text-muted-foreground">
            Changes that go out together, with one window and one decision.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm" data-testid="releases-calendar-link">
            <Link to="/calendar">Calendar</Link>
          </Button>
          <Button asChild variant="outline" size="sm" data-testid="releases-changes-link">
            <Link to="/changes">Changes</Link>
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6 space-y-8">
        {message && (
          <p className="text-sm text-amber-900" data-testid="releases-message">{message}</p>
        )}

        {board && (
          <>
            <section className="grid gap-3 md:grid-cols-5 text-sm" data-testid="releases-totals">
              {Object.entries(board.totals).map(([key, value]) => (
                <div key={key} className="border rounded-md px-3 py-2"
                     data-testid={`releases-total-${key}`}>
                  <p className="text-xs text-muted-foreground">{STATE_LABEL[key] || key}</p>
                  <p className="text-2xl font-semibold">{value}</p>
                </div>
              ))}
            </section>
            <p className="text-sm text-muted-foreground" data-testid="releases-method">
              {board.method}
            </p>

            <section className="space-y-2" data-testid="releases-list">
              <h2 className="text-base md:text-lg font-medium">Planned and past releases</h2>
              {board.releases.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="releases-empty">
                  No releases yet.
                </p>
              )}
              <ul className="space-y-2">
                {board.releases.map((r) => (
                  <li key={r.id}>
                    <button type="button" onClick={() => open(r)}
                            data-testid={`release-open-${r.number}`}
                            className="w-full text-left border rounded-md px-3 py-2 hover:bg-accent">
                      <span className="font-mono text-sm">{r.number}</span>{" "}
                      <span className="text-sm font-medium">{r.title}</span>
                      <span className="block text-xs text-muted-foreground">
                        {STATE_LABEL[r.state] || r.state} · {r.environment} ·{" "}
                        {String(r.window.start).slice(0, 16).replace("T", " ")} ·{" "}
                        {r.changes} change(s)
                        {r.overridden ? " · gate overridden" : ""}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>

            {gate && (
              <section className="space-y-3 border rounded-md p-4" data-testid="release-detail">
                <div>
                  <h2 className="text-base md:text-lg font-medium">
                    {gate.release.number} — {gate.release.title}
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    {STATE_LABEL[gate.release.state]} · {gate.release.environment} ·{" "}
                    window {String(gate.release.window.start).slice(0, 16).replace("T", " ")} to{" "}
                    {String(gate.release.window.end).slice(0, 16).replace("T", " ")}
                  </p>
                </div>

                <div className="space-y-1" data-testid="release-readiness">
                  <h3 className="text-sm font-semibold">
                    Readiness — {gate.ready ? "ready to go" : "blocked"}
                  </h3>
                  <p className="text-xs text-muted-foreground"
                     data-testid="release-readiness-method">{gate.method}</p>
                  {gate.environment_rule && (
                    <p className="text-xs text-muted-foreground"
                       data-testid="release-environment-rule">
                      In {gate.environment_rule.environment}:{" "}
                      {gate.environment_rule.required_checks.length} check(s) must pass —{" "}
                      {gate.environment_rule.source}. {gate.environment_rule.override_note}.
                    </p>
                  )}
                  <ul className="text-sm space-y-1">
                    {gate.checks.map((c) => (
                      <li key={c.key} data-testid={`release-check-${c.key}`}
                          className={c.required_here === false ? "text-muted-foreground"
                            : c.passed ? "text-emerald-900" : "text-red-900"}>
                        {c.passed ? "✓" : c.required_here === false ? "·" : "✗"} {c.title} —{" "}
                        {c.detail}
                        {c.required_here === false && (
                          <span className="block text-xs" data-testid={`release-advisory-${c.key}`}>
                            {c.not_required_note}
                          </span>
                        )}
                        {!c.passed && c.required_here !== false && (
                          <span className="block text-xs text-muted-foreground">{c.remedy}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                  {gate.override && (
                    <p className="text-xs text-amber-900" data-testid="release-override-record">
                      Gate overridden by {gate.override.by_email} — {gate.override.reason}
                    </p>
                  )}
                  {gate.approval && gate.approval.state !== "not_required" && (
                    <div className="space-y-1" data-testid="release-approval">
                      <p className="text-sm">
                        Go/no-go: <span data-testid="release-approval-state">
                          {gate.approval.state.replace("_", " ")}
                        </span>
                        {gate.approval.outstanding?.length > 0 &&
                          ` · waiting on ${gate.approval.outstanding.length} approver(s)`}
                      </p>
                      {gate.approval.decisions?.map((d) => (
                        <p key={d.number} className="text-xs text-muted-foreground">
                          {d.status} by {d.approver}
                          {d.comments ? ` — ${d.comments}` : ""}
                        </p>
                      ))}
                      {gate.release.state === "planning" && board.can_author
                        && gate.approval.state === "not_requested" && (
                        <Button size="sm" variant="outline" data-testid="release-submit"
                                onClick={() => act("/submit", {}, "Go/no-go requested")}>
                          Ask for the go/no-go
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {gate.release.state === "planning" && board.can_author && (
                  <div className="space-y-2" data-testid="release-grouping">
                    <h3 className="text-sm font-semibold">Changes in this release</h3>
                    <ul className="text-sm space-y-1">
                      {gate.changes.map((c) => (
                        <li key={c.id} className="flex items-center justify-between gap-2">
                          <span>
                            <span className="font-mono">{c.number}</span> {c.title}
                            <span className="block text-xs text-muted-foreground">
                              {c.state} · approval {c.approval_request_state || "not required"} ·{" "}
                              {c.ci_updates} CI update(s)
                            </span>
                          </span>
                          <Button size="sm" variant="ghost"
                                  data-testid={`release-remove-${c.number}`}
                                  onClick={() => toggleChange(c.id)}>Remove</Button>
                        </li>
                      ))}
                      {gate.changes.length === 0 && (
                        <li className="text-muted-foreground" data-testid="release-no-changes">
                          Nothing grouped yet.
                        </li>
                      )}
                    </ul>
                    <h3 className="text-sm font-semibold">Available changes</h3>
                    <ul className="text-sm space-y-1" data-testid="release-available-changes">
                      {board.available_changes.slice(0, 12).map((c) => (
                        <li key={c.id} className="flex items-center justify-between gap-2">
                          <span>
                            <span className="font-mono">{c.number}</span> {c.title}
                            <span className="block text-xs text-muted-foreground">
                              {c.state} · risk {c.risk_level || "not scored"} · approval{" "}
                              {c.approval_request_state || "not required"}
                            </span>
                          </span>
                          <Button size="sm" variant="outline"
                                  data-testid={`release-add-${c.number}`}
                                  onClick={() => toggleChange(c.id)}>Add</Button>
                        </li>
                      ))}
                      {board.available_changes.length === 0 && (
                        <li className="text-muted-foreground"
                            data-testid="release-no-available-changes">
                          Every open change already belongs to a release.
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                {gate.release.state === "planning" && board.can_author && (
                  <div className="space-y-2" data-testid="release-schedule-block">
                    {!gate.ready && gate.environment_rule
                      && !gate.environment_rule.allow_override ? (
                      <p className="text-xs text-red-900" data-testid="release-no-override">
                        Overriding the gate is not allowed in {gate.release.environment}. Make
                        every required check pass, or take it out in a less strict environment
                        first.
                      </p>
                    ) : (
                      <>
                        {!gate.ready && (
                          <div className="space-y-1">
                            <label htmlFor="release-override" className="text-xs font-medium">
                              Override reason (another manager only, recorded on the release)
                            </label>
                            <Input id="release-override" data-testid="release-override-reason"
                                   value={override}
                                   onChange={(e) => setOverride(e.target.value)} />
                          </div>
                        )}
                        <Button size="sm" data-testid="release-schedule" onClick={schedule}>
                          {gate.ready ? "Schedule this release" : "Override and schedule"}
                        </Button>
                      </>
                    )}
                  </div>
                )}

                {["scheduled", "deployed", "verified"].includes(gate.release.state)
                  && board.can_author && (
                  <div className="space-y-1" data-testid="release-promotion">
                    <h3 className="text-sm font-semibold">Promotion</h3>
                    <p className="text-xs text-muted-foreground">
                      Order: {(gate.environment_order || []).join(" → ")} · now in{" "}
                      {gate.release.environment}
                    </p>
                    <ul className="text-xs space-y-1" data-testid="release-promotion-history">
                      {(gate.promotions || []).map((p, i) => (
                        <li key={i} className="text-muted-foreground">
                          {p.from_environment} → {p.environment} by {p.by_email} on{" "}
                          {String(p.at).slice(0, 16).replace("T", " ")}
                        </li>
                      ))}
                      {(gate.promotions || []).length === 0 && (
                        <li className="text-muted-foreground" data-testid="release-no-promotions">
                          It has not been promoted yet.
                        </li>
                      )}
                    </ul>
                    {(() => {
                      const order = gate.environment_order || [];
                      const next = order[order.indexOf(gate.release.environment) + 1];
                      return next ? (
                        <Button size="sm" variant="outline" data-testid="release-promote"
                                onClick={() => act("/promote", { environment: next })}>
                          Promote to {next}
                        </Button>
                      ) : (
                        <p className="text-xs text-muted-foreground" data-testid="release-promote-end">
                          This is the last environment in the order.
                        </p>
                      );
                    })()}
                  </div>
                )}

                <div className="space-y-2 border-t pt-3" data-testid="release-notes-block">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold">Release notes</h3>
                    <Button size="sm" variant="outline" data-testid="release-notes-generate"
                            onClick={loadNotes}>
                      {notes ? "Refresh the notes" : "Write the notes"}
                    </Button>
                    {notes && (
                      <Button size="sm" variant="ghost" data-testid="release-notes-copy"
                              onClick={copyNotes}>
                        {copied ? "Copied" : "Copy"}
                      </Button>
                    )}
                  </div>
                  {notes && (
                    <>
                      <p className="text-xs text-muted-foreground"
                         data-testid="release-notes-method">{notes.method}</p>
                      {notes.nothing_to_report && (
                        <p className="text-xs text-amber-900" data-testid="release-notes-empty">
                          No change records are grouped yet, so the notes say only what the
                          release is for.
                        </p>
                      )}
                      <pre tabIndex={0} role="region" aria-label="Release notes text"
                           className="text-xs whitespace-pre-wrap border rounded-md p-3 bg-muted/40"
                           data-testid="release-notes-text">{notes.markdown}</pre>
                    </>
                  )}
                </div>

                {["scheduled", "deployed"].includes(gate.release.state) && board.can_author && (
                  <div className="space-y-2" data-testid="release-outcome-block">
                    <div className="space-y-1">
                      <label htmlFor="release-note" className="text-xs font-medium">
                        What happened
                      </label>
                      <Input id="release-note" data-testid="release-note" value={note}
                             onChange={(e) => setNote(e.target.value)} />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {gate.release.state === "scheduled" && (
                        <Button size="sm" data-testid="release-deployed"
                                onClick={() => outcome("deployed")}>It went out</Button>
                      )}
                      {gate.release.state === "deployed" && (
                        <>
                          <Button size="sm" data-testid="release-verified"
                                  onClick={() => outcome("verified")}>Verified good</Button>
                          <Button size="sm" variant="outline" data-testid="release-rolled-back"
                                  onClick={() => outcome("rolled_back")}>Rolled back</Button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </section>
            )}

            {board.can_author && (
              <section className="space-y-2 border-t pt-6" data-testid="release-create">
                <h2 className="text-base md:text-lg font-medium">Plan a release</h2>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-1">
                    <label htmlFor="release-title" className="text-sm font-medium">Title</label>
                    <Input id="release-title" data-testid="release-title" value={form.title}
                           onChange={(e) => setForm({ ...form, title: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="release-type" className="text-sm font-medium">Release type</label>
                    <select id="release-type" data-testid="release-type"
                            className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                            value={form.type_key}
                            onChange={(e) => setForm({ ...form, type_key: e.target.value })}>
                      {board.release_types.map((t) => (
                        <option key={t.key} value={t.key}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="release-environment" className="text-sm font-medium">
                      Environment
                    </label>
                    <select id="release-environment" data-testid="release-environment"
                            className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                            value={form.environment}
                            onChange={(e) => setForm({ ...form, environment: e.target.value })}>
                      {(board.release_types.find((t) => t.key === form.type_key)?.environments
                        || board.environments).map((e) => (
                          <option key={e} value={e}>{e}</option>
                        ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="release-rollback" className="text-sm font-medium">
                      Rollback plan
                    </label>
                    <Input id="release-rollback" data-testid="release-rollback"
                           value={form.rollback_plan}
                           onChange={(e) => setForm({ ...form, rollback_plan: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="release-start" className="text-sm font-medium">
                      Window starts
                    </label>
                    <input id="release-start" type="datetime-local" data-testid="release-start"
                           className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                           value={form.start}
                           onChange={(e) => setForm({ ...form, start: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="release-end" className="text-sm font-medium">Window ends</label>
                    <input id="release-end" type="datetime-local" data-testid="release-end"
                           className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                           value={form.end}
                           onChange={(e) => setForm({ ...form, end: e.target.value })} />
                  </div>
                </div>
                <Button size="sm" data-testid="release-create-submit" disabled={!form.title}
                        onClick={create}>Plan it</Button>
              </section>
            )}

            {where && (
              <section className="space-y-2 border-t pt-6" data-testid="release-where">
                <h2 className="text-base md:text-lg font-medium">What is where</h2>
                <p className="text-sm text-muted-foreground" data-testid="release-where-method">
                  {where.method.replace(/\*\*/g, "")}
                </p>
                <div className="grid gap-3 md:grid-cols-4 text-sm">
                  {where.environments.map((env) => (
                    <div key={env.environment} className="border rounded-md px-3 py-2"
                         data-testid={`where-${env.environment}`}>
                      <p className="text-xs text-muted-foreground">{env.environment}</p>
                      {env.latest ? (
                        <>
                          <p className="font-mono text-sm">{env.latest.number}</p>
                          <p className="text-xs">{env.latest.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {env.latest.state}
                            {env.latest.deployed_at
                              ? ` · ${String(env.latest.deployed_at).slice(0, 10)}` : ""}
                          </p>
                        </>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          Nothing recorded as landed here.
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {performance && (
              <section className="space-y-3 border-t pt-6" data-testid="release-performance">
                <h2 className="text-base md:text-lg font-medium">Delivery performance</h2>
                <p className="text-sm text-muted-foreground" data-testid="performance-method">
                  {performance.method}
                </p>
                <div className="grid gap-3 md:grid-cols-4 text-sm">
                  {[["Releases deployed", performance.releases.deployed, "deployed"],
                    ["Rollback rate", performance.releases.rollback_rate_pct === null
                      ? "no data" : `${performance.releases.rollback_rate_pct}%`, "rollback"],
                    ["Median release lead time",
                      performance.releases.median_lead_time_label || "no data", "lead"],
                    ["Gates overridden", performance.releases.overridden, "overridden"]]
                    .map(([label, value, id]) => (
                      <div key={id} className="border rounded-md px-3 py-2"
                           data-testid={`performance-${id}`}>
                        <p className="text-xs text-muted-foreground">{label}</p>
                        <p className="text-2xl font-semibold">{value}</p>
                      </div>
                    ))}
                </div>
                <div className="overflow-x-auto" tabIndex={0} role="region"
                     aria-label="Change failure rate and lead time by service">
                  <table className="w-full text-sm border rounded-md"
                         data-testid="performance-table">
                    <caption className="sr-only">
                      Change failure rate and lead time by service
                    </caption>
                    <thead>
                      <tr className="text-left border-b">
                        <th scope="col" className="px-3 py-2">Service</th>
                        <th scope="col" className="px-3 py-2">Changes</th>
                        <th scope="col" className="px-3 py-2">Failures</th>
                        <th scope="col" className="px-3 py-2">Failure rate</th>
                        <th scope="col" className="px-3 py-2">Median lead time</th>
                        <th scope="col" className="px-3 py-2">Examples</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...performance.by_service, performance.unattributed]
                        .filter((r) => r.changes > 0)
                        .map((r) => (
                          <tr key={r.service_key || "none"} className="border-b last:border-0"
                              data-testid={`performance-row-${r.service_key || "unattributed"}`}>
                            <th scope="row" className="px-3 py-2 text-left font-medium">
                              {r.service_name}
                            </th>
                            <td className="px-3 py-2">{r.changes}</td>
                            <td className="px-3 py-2">{r.failures}</td>
                            <td className="px-3 py-2">
                              {r.change_failure_rate_pct === null ? "—"
                                : `${r.change_failure_rate_pct}%`}
                            </td>
                            <td className="px-3 py-2">
                              {r.median_lead_time_hours === null ? "—"
                                : `${r.median_lead_time_hours} h`}
                            </td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">
                              {r.failure_examples.join("; ") || "—"}
                            </td>
                          </tr>
                        ))}
                      {performance.totals.changes === 0 && (
                        <tr><td colSpan={6} className="px-3 py-2 text-muted-foreground"
                                data-testid="performance-empty">
                          No changes in the last {performance.window_days} days.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
